import { MarkerService } from './marker.service';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BaseComponent } from '../../core/base-component';
import * as _ from 'lodash';
import { MtnMap } from '../mtn-map';
import PlaceResult = google.maps.places.PlaceResult;
import Point = google.maps.Point;
import Autocomplete = google.maps.places.Autocomplete;
import Marker = google.maps.Marker;
import InfoWindow = google.maps.InfoWindow;
import Size = google.maps.Size;
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';
// for now we are not using this this is for future use if we need to show line by line address
// import GeocoderAddressComponent = google.maps.GeocoderAddressComponent;

@Component({
  standalone: false,
  selector: 'mtn-google-search',
  templateUrl: './google-search.component.html',
  styleUrls: ['./google-search.component.scss']
})
export class GoogleSearchComponent extends BaseComponent implements OnInit, OnChanges {

  @Output()
  selectEvent = new EventEmitter<PlaceResult>();

  @Input()
  map: MtnMap;

  @ViewChild('searchField', { static: true })
  searchField: ElementRef;

  autoComplete: Autocomplete;
  infoWindow: InfoWindow;
  infoWindowContent: any;
  marker: Marker;
  locationUrl: string;
  searchForm = new UntypedFormGroup({
    isSearchBound: new UntypedFormControl(false),
    search: new UntypedFormControl('')
  });

  private prefLimitSearchToMapBounds = false;

  constructor(private markerService: MarkerService, private hitTracking: HitTrackingService) {
    super();
  }

  ngOnChanges(): void {
    if (this.map) {
      console.warn('Reinitializing Google Search');
      this.initMarker();
      this.initInfoWindow();
      this.initAutoComplete();
      if (this.prefLimitSearchToMapBounds) {
        this.bindSearchToMapBounds();
      }
      this.searchField.nativeElement.focus();
    }
  }

  ngOnInit() {

    setTimeout(() => {
      this.infoWindowContent = document.getElementById('google-search-info-window-content');
    });

    //TODO read user preference
    this.prefLimitSearchToMapBounds = false;
    this.searchForm.get('isSearchBound').setValue(this.prefLimitSearchToMapBounds);

    this.subscribeToBoundsRestrictionChanges();
  }

  private bindSearchToMapBounds(): void {
    this.autoComplete.setOptions({ strictBounds: true });
  }

  private getAddressComponentByType(place: PlaceResult, type: string): string {
    const component = _.find(place.address_components, (comp) => comp.types.includes(type));
    return component ? component.long_name : '';
  }

  private initAutoComplete(): void {
    this.autoComplete = new Autocomplete(this.searchField.nativeElement, {
      componentRestrictions: { country: 'US' },
      types: [] //search all types
    });
    this.autoComplete.bindTo('bounds', this.map);
    this.autoComplete.addListener('place_changed', () => {
      this.markerService.clearMarkers();

      // Close ALL info windows
      this.closeInfoWindow();

      const place: PlaceResult = this.autoComplete.getPlace();

      if (!place || !place.geometry) {
        console.warn("No place details available.");
        return;
      }
      // Extract postal code
      const postalCode = this.getAddressComponentByType(place, 'postal_code');

      // Ensure the postal code is part of the address
      let formattedAddress = place.formatted_address || '';
      if (postalCode && !formattedAddress.includes(postalCode)) {
        formattedAddress += `, ${postalCode}`;
      }


      // Use Geocoding API to resolve Plus Code to precise coordinates
      this.resolvePlusCode(place.formatted_address, (location: any) => {
        // Emit the place details with the updated address
        this.selectEvent.emit({
          ...place,
          formatted_address: formattedAddress,
        });

        // Position the map on the selected place using resolved coordinates
        this.map.setCenter(location); // Set center to the resolved coordinates
        this.map.setZoom(17); // Optional: You can adjust the zoom level

        // Set a marker on that place
        this.marker.setPosition(location);
        this.marker.setVisible(true);
        this.markerService.addMarker(this.marker);

        // Update the locationUrl within Angular's zone to trigger change detection
        this.locationUrl = `https://www.google.com/maps?q=${location.lat()},${location.lng()}`;

        // Update info window content
        if (this.infoWindowContent) {
          this.infoWindowContent.children['info-window-title'].innerHTML = formattedAddress;
          this.infoWindowContent.children['info-window-link'].href = this.locationUrl;
          this.infoWindow.setContent(this.infoWindowContent);
          this.infoWindow.open(this.map, this.marker);
        }
      });
    });
  }
  openLocationUrl() {
    if (this.locationUrl) {
      window.open(this.locationUrl, '_blank'); // Opens in a new tab
    } else {
      console.warn('No URL to open');
    }
  }
  private closeInfoWindow(): void {
    if (this.infoWindow && this.isInfoWindowOpen()) {
      this.infoWindow.close();
      // Clear the content
    }
  }

  private initInfoWindow(): void {
    if (!this.infoWindowContent) {
      this.infoWindowContent = document.getElementById('google-search-info-window-content');
    }

    this.infoWindow = new InfoWindow({
      pixelOffset: new Size(112, -85)
    });
    this.infoWindow.setContent(this.infoWindowContent);
  }

  private initMarker(): void {
    this.marker = new Marker({
      map: this.map,
      anchorPoint: new Point(this.map.getCenter().lng(), this.map.getCenter().lat()),
      visible: false
    });

    this.marker.addListener('click', () => {
      if (this.isInfoWindowOpen()) {
        this.closeInfoWindow();
      } else {
        this.infoWindow.setContent(this.infoWindowContent);
        this.infoWindow.open(this.map, this.marker);
      }
    });
  }

  private isInfoWindowOpen(): boolean {
    //Hacky way of doing this from https://stackoverflow.com/questions/12410062/check-if-infowindow-is-opened-google-maps-v3
    const windowMap = (<any>this.infoWindow).getMap();
    return (windowMap !== null && typeof windowMap !== 'undefined');
  }

  private subscribeToBoundsRestrictionChanges(): void {
    this.addSubscription(
      this.searchForm.get('isSearchBound').valueChanges
        .subscribe((value: boolean) => {
          if (value) {
            this.bindSearchToMapBounds();
          } else {
            this.unbindSearchFromMapBounds();
          }
        })
    );
  }

  private unbindSearchFromMapBounds(): void {
    this.autoComplete.setOptions({ strictBounds: false });
  }

  // Geocoding to resolve Plus Code to coordinates
  private resolvePlusCode(address: string, callback: (location: google.maps.LatLng) => void): void {
    const geocoder = new google.maps.Geocoder();

    geocoder.geocode({ 'address': address }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        const result = results[0];
        const location = result.geometry.location;
        const lat = location.lat();
        const lng = location.lng();

        const addressComponents = result.address_components;

        const getComponent = (type: string) => {
          return addressComponents.find(c => c.types.includes(type))?.long_name || null;
        };

        const addressMetadata = {
          fullAddress: result.formatted_address,
          postalCode: getComponent('postal_code'),
          state: getComponent('administrative_area_level_1'),
          city: getComponent('locality') || getComponent('sublocality'),
          country: getComponent('country'),
          lat,
          lng
        };

        this.hitTracking.hitTheUserTracking(
          USER_TRACKING_SOURCES.GOOGLE_SEARCH,
          addressMetadata,
          USER_TRACKING_ACTIONS.SEARCH,
          USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.SEARCH]
        ).subscribe(() => {});

        callback(location);
      } else {
        console.error('Geocode failed due to:', status);
      }
    });


  }
}
